import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { Card } from '../types';
import ConfirmationModal from './ConfirmationModal';
import { getDatabaseService } from '../services/DatabaseFactory';

const DeckEditor: React.FC = () => {
  const { deckId } = useParams<{ deckId: string }>();
  const { state, loadDeck, loadCards, deleteCard, updateDeck } = useApp();
  const [cards, setCards] = useState<Card[]>([]);
  const [selectedCards, setSelectedCards] = useState<Set<string>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);
  const [cardToDelete, setCardToDelete] = useState<Card | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    difficulty: 'Easy' as 'Easy' | 'Medium' | 'Hard',
    targetLanguages: [] as string[],
    categoryTags: [] as string[],
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (deckId) {
      loadDeckData();
    }
  }, [deckId]);

  const loadDeckData = async () => {
    if (!deckId) return;
    
    try {
      await loadDeck(deckId);
      await loadCards(deckId);
    } catch (error) {
      console.error('Failed to load deck data:', error);
    }
  };

  useEffect(() => {
    if (state.currentDeck) {
      setEditForm({
        name: state.currentDeck.name,
        description: state.currentDeck.description,
        difficulty: state.currentDeck.difficulty,
        targetLanguages: [...state.currentDeck.targetLanguages],
        categoryTags: [...state.currentDeck.categoryTags],
      });
    }
    setCards(state.cards);
  }, [state.currentDeck, state.cards]);

  const handleSaveDeck = async () => {
    if (!state.currentDeck) return;
    
    try {
      await updateDeck(state.currentDeck.id, editForm);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update deck:', error);
    }
  };

  const handleDeleteCard = (card: Card) => {
    setCardToDelete(card);
    setShowDeleteModal(true);
  };

  const confirmDeleteCard = async () => {
    if (cardToDelete) {
      try {
        await deleteCard(cardToDelete.id);
        setShowDeleteModal(false);
        setCardToDelete(null);
      } catch (error) {
        console.error('Failed to delete card:', error);
      }
    }
  };

  const handleClearAllCards = () => {
    setShowClearModal(true);
  };

  const confirmClearAll = async () => {
    try {
      for (const card of cards) {
        await deleteCard(card.id);
      }
      setShowClearModal(false);
    } catch (error) {
      console.error('Failed to clear cards:', error);
    }
  };

  const handleSelectCard = (cardId: string) => {
    const newSelected = new Set(selectedCards);
    if (newSelected.has(cardId)) {
      newSelected.delete(cardId);
    } else {
      newSelected.add(cardId);
    }
    setSelectedCards(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedCards.size === cards.length) {
      setSelectedCards(new Set());
    } else {
      setSelectedCards(new Set(cards.map(card => card.id)));
    }
  };

  const handleDeleteSelected = async () => {
    try {
      for (const cardId of Array.from(selectedCards)) {
        await deleteCard(cardId);
      }
      setSelectedCards(new Set());
    } catch (error) {
      console.error('Failed to delete selected cards:', error);
    }
  };

  const handleMoveCard = async (cardId: string, direction: 'up' | 'down') => {
    const currentIndex = cards.findIndex(card => card.id === cardId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= cards.length) return;

    const newCards = [...cards];
    [newCards[currentIndex], newCards[newIndex]] = [newCards[newIndex], newCards[currentIndex]];
    
    // Update local state immediately for responsive UI
    setCards(newCards);

    // Persist the new order to database
    try {
      const cardIds = newCards.map(card => card.id);
      const db = await getDatabaseService();
      await db.updateCardOrder(deckId!, cardIds);
    } catch (error) {
      console.error('Failed to update card order:', error);
      // Revert local state on error
      setCards(cards);
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900';
      case 'Medium': return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900';
      case 'Hard': return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900';
      default: return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900';
    }
  };

  const languages = ['Java', 'JavaScript', 'Python', 'C++', 'C#', 'Go', 'Rust', 'TypeScript'];
  const categories = ['Arrays', 'Strings', 'Loops', 'Conditionals', 'Functions', 'Objects', 'Math', 'Algorithms'];

  const toggleLanguage = (language: string) => {
    setEditForm(prev => ({
      ...prev,
      targetLanguages: prev.targetLanguages.includes(language)
        ? prev.targetLanguages.filter(l => l !== language)
        : [...prev.targetLanguages, language]
    }));
  };

  const toggleCategory = (category: string) => {
    setEditForm(prev => ({
      ...prev,
      categoryTags: prev.categoryTags.includes(category)
        ? prev.categoryTags.filter(c => c !== category)
        : [...prev.categoryTags, category]
    }));
  };

  if (state.loading || !state.currentDeck) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="flex items-center space-x-2">
          <div className="spinner"></div>
          <span className="text-gray-600 dark:text-gray-400">Loading deck...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <Link
                to="/"
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </Link>
              <div>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                    className="text-3xl font-bold bg-transparent border-none p-0 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                    autoFocus
                  />
                ) : (
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                    {state.currentDeck.name}
                  </h1>
                )}
                {isEditing ? (
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                    className="mt-2 w-full bg-transparent border border-gray-300 dark:border-gray-600 rounded p-2 text-gray-600 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    placeholder="Deck description"
                  />
                ) : (
                  <p className="text-gray-600 dark:text-gray-400 mt-2">
                    {state.currentDeck.description}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {isEditing ? (
                <>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      if (state.currentDeck) {
                        setEditForm({
                          name: state.currentDeck.name,
                          description: state.currentDeck.description,
                          difficulty: state.currentDeck.difficulty,
                          targetLanguages: [...state.currentDeck.targetLanguages],
                          categoryTags: [...state.currentDeck.categoryTags],
                        });
                      }
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveDeck}
                    className="btn btn-primary"
                  >
                    Save
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="btn btn-secondary"
                  >
                    Edit Deck
                  </button>
                  <Link
                    to={`/deck/${deckId}/play`}
                    className="btn btn-primary"
                  >
                    Study
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Deck Info */}
          {isEditing ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div>
                <label className="form-label">Difficulty</label>
                <select
                  value={editForm.difficulty}
                  onChange={(e) => setEditForm(prev => ({ ...prev, difficulty: e.target.value as any }))}
                  className="form-select"
                >
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="Hard">Hard</option>
                </select>
              </div>
              
              <div>
                <label className="form-label">Target Languages</label>
                <div className="flex flex-wrap gap-2">
                  {languages.map(language => (
                    <button
                      key={language}
                      type="button"
                      onClick={() => toggleLanguage(language)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        editForm.targetLanguages.includes(language)
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {language}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="form-label">Categories</label>
                <div className="flex flex-wrap gap-2">
                  {categories.map(category => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => toggleCategory(category)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        editForm.categoryTags.includes(category)
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(state.currentDeck.difficulty)}`}>
                {state.currentDeck.difficulty}
              </span>
              <div className="flex flex-wrap gap-2">
                {state.currentDeck.targetLanguages.map(language => (
                  <span
                    key={language}
                    className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 rounded text-sm"
                  >
                    {language}
                  </span>
                ))}
              </div>
              <span className="text-gray-500 dark:text-gray-400">
                {cards.length} cards
              </span>
            </div>
          )}

          {/* Actions Bar */}
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <Link
                to={`/deck/${deckId}/card/new`}
                className="btn btn-primary"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Card
              </Link>
              
              {selectedCards.size > 0 && (
                <button
                  onClick={handleDeleteSelected}
                  className="btn btn-danger"
                >
                  Delete Selected ({selectedCards.size})
                </button>
              )}
              
              {cards.length > 0 && (
                <button
                  onClick={handleClearAllCards}
                  className="btn btn-warning"
                >
                  Clear All
                </button>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedCards.size === cards.length && cards.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">Select All</span>
              </label>
            </div>
          </div>
        </div>

        {/* Cards List */}
        {cards.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No cards yet</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Get started by adding your first flash card
            </p>
            <div className="mt-6">
              <Link
                to={`/deck/${deckId}/card/new`}
                className="btn btn-primary"
              >
                Add Card
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {cards.map((card, index) => (
              <div
                key={card.id}
                className={`card hover:shadow-lg transition-shadow ${
                  selectedCards.has(card.id) ? 'ring-2 ring-blue-500 ring-opacity-50' : ''
                }`}
              >
                <div className="card-body">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <label className="custom-checkbox mt-1">
                        <input
                          type="checkbox"
                          checked={selectedCards.has(card.id)}
                          onChange={() => handleSelectCard(card.id)}
                        />
                        <span className="checkmark"></span>
                      </label>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {card.name}
                          </h3>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(card.difficulty)}`}>
                              {card.difficulty}
                            </span>
                            <span className="px-2 py-1 bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 rounded text-xs">
                              {card.category}
                            </span>
                          </div>
                        </div>
                        
                        <p className="text-gray-600 dark:text-gray-400 mb-3">
                          {card.question}
                        </p>
                        
                        <div className="flex flex-wrap gap-2 mb-3">
                          {Object.keys(card.languages).map(language => (
                            <span
                              key={language}
                              className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 rounded text-xs"
                            >
                              {language}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleMoveCard(card.id, 'up')}
                        disabled={index === 0}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleMoveCard(card.id, 'down')}
                        disabled={index === cards.length - 1}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      <Link
                        to={`/deck/${deckId}/card/${card.id}/edit`}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </Link>
                      <button
                        onClick={() => handleDeleteCard(card)}
                        className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900 text-red-600 dark:text-red-400"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Delete Card Modal */}
        {showDeleteModal && cardToDelete && (
          <ConfirmationModal
            title="Delete Card"
            message={`Are you sure you want to delete "${cardToDelete.name}"? This action cannot be undone.`}
            confirmText="Delete"
            confirmButtonClass="btn-danger"
            onConfirm={confirmDeleteCard}
            onCancel={() => {
              setShowDeleteModal(false);
              setCardToDelete(null);
            }}
          />
        )}

        {/* Clear All Modal */}
        {showClearModal && (
          <ConfirmationModal
            title="Clear All Cards"
            message={`Are you sure you want to delete all ${cards.length} cards in this deck? This action cannot be undone.`}
            confirmText="Clear All"
            confirmButtonClass="btn-danger"
            onConfirm={confirmClearAll}
            onCancel={() => setShowClearModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default DeckEditor;